import { useState } from "react";
import { HelpCircle, Trash2, X, Check } from "lucide-react";
import type {
  Transaction,
  ExpenseCategory,
  IncomeCategory,
  ClassifyIncomingRequest,
} from "@boo/shared";
import { CATEGORY_COLORS } from "../../types";
import CategoryEditor from "./CategoryEditor";
import IncomingReviewRow from "./IncomingReviewRow";

interface UncategorizedSectionProps {
  transactions: Transaction[];
  onCategorize: (
    id: string,
    category: ExpenseCategory | IncomeCategory,
  ) => void;
  onClassifyIncoming: (
    id: string,
    payload: ClassifyIncomingRequest,
  ) => Promise<void>;
  onDelete: (id: string) => void;
}

function formatBaht(n: number) {
  return `฿${n.toLocaleString("en-US")}`;
}

export default function UncategorizedSection({
  transactions,
  onCategorize,
  onClassifyIncoming,
  onDelete,
}: UncategorizedSectionProps) {
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [resolved, setResolved] = useState<Set<string>>(new Set());
  const [selectedCategories, setSelectedCategories] = useState<
    Record<string, ExpenseCategory | IncomeCategory>
  >({});
  const [confirming, setConfirming] = useState<string | null>(null);

  if (transactions.length === 0) return null;

  const remaining = transactions.length - resolved.size;

  const handleCategorize = (
    id: string,
    category: ExpenseCategory | IncomeCategory,
  ) => {
    onCategorize(id, category);
    setResolved((prev) => new Set([...prev, id]));
    setSelectedCategories((prev) => ({ ...prev, [id]: category }));
  };

  return (
    <div className="rounded-xl p-4 mb-5 bg-[var(--color-primary-bg)] border border-dashed border-[var(--color-primary-border)]">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="flex items-center gap-1.5 text-[13px] font-bold m-0 mb-1 text-[var(--color-primary-900)]">
            <HelpCircle className="w-3.5 h-3.5 text-[var(--color-primary-600)]" />
            Needs a category
          </h3>
          <p className="text-[12px] leading-snug m-0 text-[var(--color-primary-800)]">
            {transactions.length} transaction
            {transactions.length > 1 ? "s" : ""} tagged{" "}
            <span className="inline-flex items-center px-2 py-0.5 rounded-[6px] text-[10px] font-medium ml-1 bg-[var(--border-color)] text-[var(--text-secondary)]">
              other
            </span>{" "}
            — pick a type to keep totals accurate.
          </p>
        </div>
        <span
          className={`text-[10px] font-medium px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0 transition-colors ${
            remaining === 0
              ? "bg-[var(--color-income)] text-white"
              : "bg-[var(--color-primary-400)] text-[#412402]"
          }`}
        >
          {remaining === 0 ? "all sorted ✓" : `${remaining} left`}
        </span>
      </div>

      {/* Uncategorized transactions */}
      <div className="flex flex-col gap-2">
        {transactions.map((tx) => {
          if (tx.needsReview) {
            return (
              <IncomingReviewRow
                key={tx.id}
                transaction={tx}
                onClassify={onClassifyIncoming}
                onDelete={onDelete}
              />
            );
          }
          const isResolved = resolved.has(tx.id);
          const dotColor =
            CATEGORY_COLORS[tx.category as keyof typeof CATEGORY_COLORS] ??
            "#B4B2A9";

          return (
            <div
              key={tx.id}
              className={`grid items-center gap-2.5 px-3 py-3.5 sm:gap-3.5 sm:px-4 rounded-[10px] transition-opacity grid-cols-[10px_1fr_auto] bg-card border-[0.5px] border-[var(--color-primary-border)] ${
                isResolved ? "opacity-[0.55]" : "opacity-100"
              }`}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: dotColor }}
              />

              <div className="flex flex-col gap-2 min-w-0">
                <div className="text-[13px] font-medium truncate text-foreground">
                  {tx.title}
                </div>
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span>{tx.source.replace("_", " ")}</span>
                  <span className="w-0.5 h-0.5 rounded-full bg-neutral-400 inline-block" />
                  <span>
                    {tx.date
                      .slice(5)
                      .replace("-", " mar ·")
                      .replace(
                        /^(\d{2}) mar · (\d+)$/,
                        (_, d) => `${d} mar · ${tx.time ?? ""}`,
                      )
                      .replace(/^(\d{2}) /, (_, d) => `${parseInt(d)} mar · `)}
                  </span>
                </div>
                {!isResolved && (
                  <input
                    type="text"
                    placeholder="add a note (optional)… e.g. lunch with team"
                    className="text-[12px] rounded-lg px-2.5 py-1.5 w-full outline-none transition-colors bg-[var(--surface-muted)] border border-transparent text-foreground focus:border-primary focus:bg-card"
                    value={notes[tx.id] ?? ""}
                    onChange={(e) =>
                      setNotes((prev) => ({ ...prev, [tx.id]: e.target.value }))
                    }
                    aria-label="Transaction note"
                  />
                )}
              </div>

              <div className="flex flex-col items-end gap-1.5">
                <CategoryEditor
                  transactionId={tx.id}
                  currentCategory={selectedCategories[tx.id] ?? null}
                  transactionType={tx.transactionType}
                  isLarge
                  onCategorize={handleCategorize}
                />
                <div className="flex items-center gap-1.5">
                  <div className="text-[14px] font-bold tabular-nums text-[var(--color-expense)]">
                    {formatBaht(tx.amount)}
                  </div>
                  {confirming === tx.id ? (
                    <>
                      <button
                        onClick={() => setConfirming(null)}
                        className="p-2 sm:p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
                        aria-label="Cancel delete"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => onDelete(tx.id)}
                        className="p-2 sm:p-1 rounded-md text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors cursor-pointer"
                        aria-label="Confirm delete"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirming(tx.id)}
                      className="p-2 sm:p-1 rounded-md text-muted-foreground opacity-40 hover:opacity-100 hover:text-red-500 hover:bg-[var(--surface-muted)] transition-all cursor-pointer"
                      aria-label="Delete transaction"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
